import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (password: string) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const { exp } = JSON.parse(atob(token.split('.')[1]));
        if (exp * 1000 > Date.now()) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('auth_token');
        }
      } catch (e) {
        localStorage.removeItem('auth_token');
      }
    }
  }, []);

  const login = (password: string) => {
    const adminPassword = import.meta.env.VITE_ADMIN_PASSWORD;
    if (password === adminPassword) {
      // Create a JWT token
      const now = Date.now();
      const expiresIn = parseInt(import.meta.env.VITE_TOKEN_EXPIRY || '3600') * 1000; // Convert to milliseconds
      const exp = Math.floor((now + expiresIn) / 1000);
      const token = btoa(JSON.stringify({
        iat: Math.floor(now / 1000),
        exp,
        role: 'admin'
      }));
      
      localStorage.setItem('auth_token', `dummy.${token}.dummy`);
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}; 